import React, { useEffect, useState } from 'react';
import { Button, Form, Row } from 'react-bootstrap';
import DataTable from '../BranchProductInventory/DataTable';
import axiosInstance from 'utils/axiosInterceptors';
import { useBranchContext } from 'providers/BranchProvider';
import { ColumnDef } from '@tanstack/react-table';
import useFetchSupplier from './useFetchSupplier';
import { formatDate, formatNumberWithCommas } from 'utils/formats';
import CreateSupplierPaymentForm, {
  TCreateSupplierPaymentFormProps
} from './CreateSupplierPaymentForm';
import CustomHookFormModal from 'components/modals/CustomHookFormModal';
import CustomModal from 'components/modals/CustomModal';
import UploadBankStatementForm, {
  UploadBankStatementFormHandle
} from './UploadBankStatementForm';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DatePicker from 'components/base/DatePicker';
import { formatToUTCISO } from 'utils/formatToUTCISO';
import BadgeStatus from 'utils/BadgeStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import { AxiosError } from 'axios';
import { useAuth } from 'hooks/useAuth';
import { FeatureRoles, hasAnyRole } from 'utils/roleAccess';

type TSupplierPayments = {
  supplierPaymentId: number;
  accountDate: string;
  supplierId: number;
  supplierName: string;
  createdOn: string;
  supplierPaymentMethod: string;
  amount: number;
  status: string;
  referenceNumber: string;
  checkNumber?: string;
};

export type TSupplierPaymentDetails = {
  supplierPaymentMethod: string;
  accountDate: Date | string;
  supplierId: number;
  procurementInvoiceIds: (number | undefined)[];
  procurementReturnIds: (number | undefined)[];
  paymentNotes?: string;
};

type SupplierPaymentFilters = {
  [key: string]: string | undefined;
  supplierId: string | undefined;
  status: string | undefined;
  supplierPaymentMethod: string | undefined;
  accountDateFrom: string | undefined;
  accountDateTo: string | undefined;
};

const SupplierPayment = () => {
  const { selectedBranch } = useBranchContext();
  const { token } = useAuth();
  const isAdmin = hasAnyRole(token?.roles, FeatureRoles.adminOnly);
  const { suppliers } = useFetchSupplier();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<TSupplierPayments | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [bankStatementDone, setBankStatementDone] = useState(false);
  const modalRef = React.useRef<{ show: () => void; hide: () => void }>({
    show: () => {},
    hide: () => {}
  });
  const bankStatementModalRef = React.useRef<{
    show: () => void;
    hide: () => void;
  }>({
    show: () => {},
    hide: () => {}
  });
  const deletePaymentModalRef = React.useRef<{
    show: () => void;
    hide: () => void;
  }>({
    show: () => {},
    hide: () => {}
  });
  const formRef = React.useRef<TCreateSupplierPaymentFormProps | null>(null);
  const bankStatementFormRef =
    React.useRef<UploadBankStatementFormHandle | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const [filters, setFilters] = useState<SupplierPaymentFilters>({
    supplierId: queryParams.get('supplierId') || undefined,
    status: queryParams.get('status') || undefined,
    supplierPaymentMethod:
      queryParams.get('supplierPaymentMethod') || undefined,
    accountDateFrom: queryParams.get('accountDateFrom') || undefined,
    accountDateTo: queryParams.get('accountDateTo') || undefined
  });
  const [accountDateRange, setAccountDateRange] = useState<
    string[] | undefined
  >(undefined);
  const supplierPaymentMethod = [
    { value: 'Check', label: 'Check' },
    { value: 'Cash', label: 'Cash' },
    { value: 'BankTransfer', label: 'Bank Transfer' }
  ];

  const status = [
    { value: 'New', label: 'New' },
    { value: 'Reviewed', label: 'Reviewed' },
    { value: 'AwaitingCheckIssuance', label: 'Awaiting Check Issuance' },
    { value: 'AwaitingBankTransfer', label: 'Awaiting Bank Transfer' },
    { value: 'CheckReady', label: 'Check Ready' },
    { value: 'CheckReleased', label: 'Check Released' },
    { value: 'Paid', label: 'Paid' }
  ];

  const fetchData = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    if (selectedBranch?.branchDomain) {
      const { data } = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain as string}/supplierPayments`,
        {
          params: {
            page,
            pageSize,
            search,
            orderBy,
            orderByDesc,
            ...filters
          }
        }
      );
      return data;
    }
    return null;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterChange = (key: string, value: any) => {
    const newFilters = {
      ...filters,
      [key]: value
    };
    setFilters(newFilters);
    const newQueryParams = new URLSearchParams(location.search);
    Object.keys(newFilters).forEach(filterKey => {
      if (newFilters[filterKey]) {
        newQueryParams.set(filterKey, newFilters[filterKey] as string);
      } else {
        newQueryParams.delete(filterKey);
      }
    });
    navigate({ search: newQueryParams.toString() });
  };

  const handleSubmitPayment = async () => {
    if (formRef.current) {
      await formRef.current.submitForm();
      setIsSubmitted(true);
    }
  };

  const handleUploadBankStatement = async () => {
    if (!bankStatementFormRef.current) return;

    const outcome = await bankStatementFormRef.current.submitForm();
    if (outcome === 'close') {
      bankStatementModalRef.current.hide();
      setBankStatementDone(false);
      bankStatementFormRef.current.reset();
    } else if (outcome === 'results') {
      setBankStatementDone(true);
    }
  };

  const openBankStatementUpload = () => {
    setBankStatementDone(false);
    bankStatementFormRef.current?.reset();
    bankStatementModalRef.current.show();
  };

  const closeBankStatementUpload = () => {
    bankStatementModalRef.current.hide();
    setBankStatementDone(false);
    bankStatementFormRef.current?.reset();
  };

  const columns: ColumnDef<TSupplierPayments>[] = [
    {
      id: 'supplierName',
      header: 'Supplier Name',
      accessorKey: 'supplierName',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return (
          <Link to={`/supplier-payment-details/${original.supplierPaymentId}`}>
            {original.supplierName}
          </Link>
        );
      }
    },
    {
      id: 'createdOn',
      header: 'Created',
      accessorKey: 'createdOn',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const formattedDate =
          original.createdOn && formatDate(original.createdOn);
        return <span>{formattedDate}</span>;
      }
    },
    {
      id: 'accountDate',
      header: 'Account Date',
      accessorKey: 'accountDate',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const formattedDate =
          original.accountDate && formatDate(original.accountDate);
        return <span>{formattedDate}</span>;
      }
    },
    {
      id: 'supplierPaymentMethod',
      header: 'Payment Method',
      accessorKey: 'supplierPaymentMethod',
      enableSorting: false
    },
    {
      id: 'amount',
      header: 'Amount',
      accessorKey: 'amount',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return <span>{formatNumberWithCommas(original.amount)}</span>;
      }
    },
    {
      id: 'checkNumber',
      header: 'Check Number',
      accessorKey: 'checkNumber',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const checkNumber =
          original.checkNumber ||
          (original.supplierPaymentMethod === 'Check'
            ? original.referenceNumber
            : undefined);
        return <span>{checkNumber || '—'}</span>;
      }
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return <BadgeStatus status={original.status} />;
      }
    },
    ...(isAdmin
      ? [
          {
            id: 'delete',
            header: 'Delete',
            enableSorting: false,
            cell: ({ row: { original } }: { row: { original: TSupplierPayments } }) => (
              <FontAwesomeIcon
                icon={faTrash}
                style={{ cursor: 'pointer', color: '#C63C51' }}
                title="Delete supplier payment"
                onClick={() => {
                  setPaymentToDelete(original);
                  deletePaymentModalRef.current.show();
                }}
              />
            )
          } satisfies ColumnDef<TSupplierPayments>
        ]
      : [])
  ];

  const createPayment = () => {
    modalRef.current.show();
  };

  const handleDeletePayment = async () => {
    if (!paymentToDelete || !selectedBranch?.branchDomain) return;

    setIsDeleting(true);
    try {
      await axiosInstance.delete(
        `/api/v1/${selectedBranch.branchDomain}/supplierPayments/${paymentToDelete.supplierPaymentId}`
      );
      toast.success('Supplier payment deleted.', {
        ...toastConfig,
        autoClose: 2000
      });
      deletePaymentModalRef.current.hide();
      setPaymentToDelete(null);
      setIsSubmitted(true);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage ||
            'Failed to delete supplier payment.',
          { ...toastConfig, autoClose: 3000 }
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (isSubmitted) {
      setIsSubmitted(false);
    }
  }, [isSubmitted]);

  return (
    <>
      <div className="d-flex justify-content-between me-5 mb-3">
        <Row lg="1" className="w-50">
          <h4>Supplier Payments</h4>
        </Row>
        <Row lg="auto" className="g-2">
          <Button
            variant="phoenix-secondary"
            className="w-10 mb-3 me-2"
            onClick={openBankStatementUpload}
          >
            Upload Bank Statement
          </Button>
          <Button
            variant="primary"
            className="w-10 mb-3"
            onClick={createPayment}
          >
            Create Payment
          </Button>
        </Row>
      </div>
      <DataTable
        queryKey={[
          'supplierPayments',
          isSubmitted,
          filters.supplierId,
          filters.status,
          filters.supplierPaymentMethod,
          filters.accountDateTo,
          selectedBranch?.branchDomain
        ]}
        queryFn={fetchData}
        columns={columns}
        rightSideActions={
          <>
            <Form.Select
              style={{
                height: '2.4rem',
                width: '15%'
              }}
              value={filters.supplierId}
              onChange={e => handleFilterChange('supplierId', e.target.value)}
            >
              <option value="">All Supplier</option>
              {suppliers?.map(data => (
                <option key={data.supplierId} value={data.supplierId}>
                  {data.name}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              style={{
                height: '2.4rem',
                width: '15%'
              }}
              value={filters.supplierPaymentMethod}
              onChange={e =>
                handleFilterChange('supplierPaymentMethod', e.target.value)
              }
            >
              <option value="">Payment Methods</option>
              {supplierPaymentMethod.map(data => (
                <option key={data.value} value={data.value}>
                  {data.label}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              style={{
                height: '2.4rem',
                width: '15%'
              }}
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}
            >
              <option value="">Status</option>
              {status?.map((option, index) => (
                <option value={option.value} key={index}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <div style={{ width: '17%', height: '2.4rem' }}>
              <DatePicker
                options={{
                  mode: 'range',
                  dateFormat: 'Y-m-d'
                }}
                placeholder="Date"
                value={accountDateRange}
                onChange={date => {
                  const dates = date.map(
                    d => formatToUTCISO(d).split('T', 1)[0]
                  );
                  console.log(dates);
                  handleFilterChange('accountDateFrom', dates[0]);
                  handleFilterChange('accountDateTo', dates[1]);
                  setAccountDateRange([dates[0], dates[1]]);
                }}
              />
            </div>
          </>
        }
      />
      <CustomHookFormModal
        ref={modalRef}
        title="Create Payment"
        content={<CreateSupplierPaymentForm ref={formRef} />}
        onCancel={() => modalRef.current.hide()}
        onSubmit={handleSubmitPayment}
      />
      <CustomModal
        ref={bankStatementModalRef}
        title="Upload Bank Statement"
        modalSize="lg"
        content={
          <UploadBankStatementForm
            ref={bankStatementFormRef}
            branchDomain={selectedBranch?.branchDomain as string}
            onUploaded={() => setIsSubmitted(true)}
          />
        }
        onCancel={closeBankStatementUpload}
        onSubmit={handleUploadBankStatement}
        onSubmitCustomDisplay={bankStatementDone ? 'Done' : 'Upload'}
      />
      <CustomModal
        ref={deletePaymentModalRef}
        title="Delete supplier payment"
        content={
          <p>
            Delete payment #{paymentToDelete?.supplierPaymentId} for{' '}
            {paymentToDelete?.supplierName}? Attached invoices and returns will be
            removed from the payment, and invoices will be marked unpaid.
          </p>
        }
        onCancel={() => {
          deletePaymentModalRef.current.hide();
          setPaymentToDelete(null);
        }}
        onSubmit={handleDeletePayment}
        onSubmitCustomDisplay={isDeleting ? 'Deleting…' : 'Delete'}
      />
    </>
  );
};

export default SupplierPayment;
